/**
 * Agent status presentation helpers.
 * Operator-facing labels: Running / Paused / Error / Waiting.
 * Never invent statuses — map only known API/realtime values.
 */

/** Canonical presentation statuses on the desk */
export type AgentPresentationStatus = 'running' | 'paused' | 'error' | 'waiting'

/** Raw statuses from REST (AITradingAgent) and realtime (RealtimeAgentStatus) */
export type AgentRawStatus =
  | 'active'
  | 'paused'
  | 'stopped'
  | 'error'
  | 'training'
  | string

export interface AgentStatusMeta {
  /** Machine key for styling / filters */
  key: AgentPresentationStatus
  /** Human label (color + text; never color alone) */
  label: string
  /** Short operator description */
  description: string
}

const STATUS_META: Record<AgentPresentationStatus, AgentStatusMeta> = {
  running: {
    key: 'running',
    label: 'Running',
    description: 'Agent is active and may place or manage orders',
  },
  paused: {
    key: 'paused',
    label: 'Paused',
    description: 'Agent is paused; no new autonomous actions',
  },
  error: {
    key: 'error',
    label: 'Error',
    description: 'Agent reported an error and needs attention',
  },
  waiting: {
    key: 'waiting',
    label: 'Waiting',
    description: 'Agent is stopped, training, or not yet ready',
  },
}

/**
 * Map API / realtime status strings to presentation keys.
 * Unknown values → waiting (honest “not running” bucket, not a fake “active”).
 */
export function toPresentationStatus(raw: AgentRawStatus | null | undefined): AgentPresentationStatus {
  if (!raw) return 'waiting'
  const s = String(raw).toLowerCase().trim()
  switch (s) {
    case 'active':
    case 'running':
    case 'run':
      return 'running'
    case 'paused':
    case 'pause':
      return 'paused'
    case 'error':
    case 'failed':
    case 'fault':
      return 'error'
    case 'stopped':
    case 'stop':
    case 'training':
    case 'idle':
    case 'pending':
    case 'waiting':
    case 'starting':
      return 'waiting'
    default:
      return 'waiting'
  }
}

export function getAgentStatusMeta(raw: AgentRawStatus | null | undefined): AgentStatusMeta {
  return STATUS_META[toPresentationStatus(raw)]
}

export function getPresentationStatusMeta(key: AgentPresentationStatus): AgentStatusMeta {
  return STATUS_META[key]
}

/** Stable legend order for status chips in the module bezel */
export const AGENT_STATUS_LEGEND: AgentPresentationStatus[] = [
  'running',
  'paused',
  'error',
  'waiting',
]
